import { Button } from '@/components/ui/button';
import { LoyaltyBrandMark } from './LoyaltyBrandMark';
import { useTranslation } from '@/i18n';
import type { LoyaltyConfiguration } from '@/api/types';

interface LoyaltyPromptProps {
    configuration: LoyaltyConfiguration;
    /** The programme's own mark. Null when the brand has not uploaded one - then its name is used. */
    logoUrl: string | null;
    onSignIn: () => void;
    onSkip: () => void;
}

/**
 * "Are you a member?", between the check and the tip.
 *
 * Skipping is a real, equal choice and looks like one. Most guests at most restaurants are not members, and a
 * screen that makes declining feel like a mistake taxes every one of them on the way to paying.
 */
export function LoyaltyPrompt({ configuration, logoUrl, onSignIn, onSkip }: LoyaltyPromptProps) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2 text-center">
                <LoyaltyBrandMark logoUrl={logoUrl} programName={configuration.programName} />

                {/* The restaurant's own words, so it is not translated - see the note in `messages.ts`. */}
                <p className="text-[0.9375rem] text-muted-foreground text-pretty">
                    {configuration.checkoutPrompt}
                </p>
            </div>

            <div className="flex flex-col gap-3">
                <Button size="lg" onClick={onSignIn}>
                    {t('loyalty.signIn')}
                </Button>

                {/*
                  * Only when the brand both allows joining and has somewhere to send them. A button that
                  * opens nothing is worse than no button.
                  */}
                {configuration.allowSignUp && configuration.signUpUrl && (
                    <a
                        href={configuration.signUpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-12 items-center justify-center rounded-xl border border-border bg-background px-5 py-3 text-center font-medium text-foreground shadow-xs"
                    >
                        {t('loyalty.join')}
                        <span className="sr-only"> ({t('receipt.feedbackExternal')})</span>
                    </a>
                )}

                <Button variant="ghost" size="lg" onClick={onSkip}>
                    {t('loyalty.skip')}
                </Button>
            </div>
        </div>
    );
}
